import { apiService } from '../services/apiService';
import { paginationService } from '../services/paginationService';
import { getUsersByWorkplace } from 'core/API/checklist';
import { assignmentWorkplaceUserOptions } from 'core/utils/enrichWorkplaceUsers';

const getWorkplaceId = (): number => {
	const id = localStorage.getItem('workplace-id');
	return id ? Number(id) : 1;
};

const getCurrentUserId = (): number => {
	const id = localStorage.getItem('userId');
	return id ? Number(id) : 1;
};

export interface IToDoPost {
	description: string;
	log: any | null;
	user: string | null;
	dueDate: string;
	note: string;
	organization: string;
	geolocation: string[];
	workplace: string;
	priority: string; // Now an API endpoint like "/api/priorities/3"
	status: string; // Now an API endpoint like "/api/statuses/1"
	assignedGroup?: string | null;
	author?: string;
}

export interface ITodoCategory {
	'@context'?: string;
	'@id'?: string;
	'@type'?: string;
	id: number;
	label: string;
}


export interface GetToDosParams {
	workplaceId?: number;
	orderBy?: string;
	direction?: 'ASC' | 'DESC';
	limit?: number;
	page?: number;
	itemsPerPage?: number;
}
 
export const PostToDo = async (body: Partial<IToDoPost>): Promise<any> => {
	const workplaceId = getWorkplaceId();
	const userId = getCurrentUserId();
	const payload: any = {
		...body,
		workplace: body?.workplace || `/api/workplaces/${workplaceId}`,
		author: body?.author || `/api/users/${userId}`,
	};
	const { ...data } = apiService.responseHandler(await apiService.post(`to_dos`, payload));

	return data;
};

// Get Categorized Todos API
export const getCategorizedToDos = async ({
	page = 1,
	itemsPerPage = 10,
	workplaceId,
}: GetToDosParams): Promise<any> => {
	const wp = workplaceId ?? getWorkplaceId();
	const query = `todos/categorized?page=${page}&itemsPerPage=${itemsPerPage}&pagination=false&workplace=${wp}`;
	const { ...data } = apiService.responseHandler(await apiService.get(query));
	return data;
};

const extractPaginatedMembers = (data: unknown): any[] => {
	if (Array.isArray(data)) {
		return data;
	}
	if (data && typeof data === 'object') {
		const record = data as Record<string, unknown>;
		if (Array.isArray(record.member)) {
			return record.member;
		}
		if (Array.isArray(record['hydra:member'])) {
			return record['hydra:member'];
		}
	}
	return [];
};

// Get all outstanding to-dos till date (no date filter)
export const getAllOutstandingTodos = async (workplace?: number): Promise<any[]> => {
	try {
		const wp = workplace ?? getWorkplaceId();
		const data = await paginationService.getWithHandler(
			`to_dos/outstanding_business_day?workplace=${wp}&orderBy[id]=desc`,
			undefined,
			30,
		);
		return extractPaginatedMembers(data);
	} catch (error) {
		console.error('Error in getAllOutstandingTodos:', error);
		throw error;
	}
};

// Get current user's outstanding to-dos till date (no date filter)
export const getMyOutstandingTodos = async (workplace?: number): Promise<any[]> => {
	try {
		const wp = workplace ?? getWorkplaceId();
		const data = await paginationService.getWithHandler(
			`to_dos/my_outstanding_business_day?workplace=${wp}&orderBy[id]=desc`,
			undefined,
			30,
		);
		return extractPaginatedMembers(data);
	} catch (error) {
		console.error('Error in getMyOutstandingTodos:', error);
		throw error;
	}
};

export const getToDoPrioritys = async (): Promise<any> => {
	const wp = getWorkplaceId();
	return paginationService.getWithHandler(`priorities?workplace=${wp}`, undefined, 50);
};


export const getToDoStatus = async (): Promise<any> => {
	const wp = getWorkplaceId();
	return paginationService.getWithHandler(`statuses?workplace=${wp}`, undefined, 50);
};


export const getToDoUser = async (): Promise<any> => {
	const wp = getWorkplaceId();
	// Use centralized workplace-scoped users service and return normalized array
	const users = await getUsersByWorkplace(wp, assignmentWorkplaceUserOptions);
	return users;
};


export const getTodoById = async (id: number): Promise<any> => {
	const response = await apiService.get(`to_dos/${id}`);
	const data = await apiService.responseHandler(response);
	return data;
};

export const UpdateToDo = async (id: number, body: any): Promise<any> => {
  const response = await apiService.put(`to_dos/${id}`, body);
  return apiService.responseHandler(response);
};

export const DeleteToDo = async (id: number): Promise<any> => {
  const response = await apiService.delete(`to_dos/${id}`);
  return apiService.responseHandler(response);
};

// Get Checklist Groups for Todo Assignment
export const getChecklistGroupsForTodo = async (): Promise<any> => {
  const wp = getWorkplaceId();
  return paginationService.getWithHandler(`checklist_groups?workplace=${wp}`, undefined, 30);
};

const normalizeTodoCategories = (data: any): ITodoCategory[] => {
	if (Array.isArray(data)) {
		return data;
	}

	if (data && typeof data === 'object') {
		if (Array.isArray(data.member)) {
			return data.member;
		}

		if (Array.isArray(data['hydra:member'])) {
			return data['hydra:member'];
		}

		if (Array.isArray(data.data)) {
			return data.data;
		}
	}

	return [];
};

export const getAllTodoCategories = async (): Promise<ITodoCategory[]> => {
	const wp = getWorkplaceId();
	const data = await paginationService.getWithHandler(`todo_categories?workplace=${wp}`);
	return normalizeTodoCategories(data);
};

export const getTodoCategoryById = async (id: number): Promise<ITodoCategory> => {
	const { ...data } = apiService.responseHandler(await apiService.get(`todo_categories/${id}`));
	return data;
};

export const createTodoCategory = async (payload: { label: string }): Promise<ITodoCategory> => {
	const wp = getWorkplaceId();
	const userId = getCurrentUserId();
	const body = {
		label: payload.label,
		workplace: `/api/workplaces/${wp}`,
		author: `/api/users/${userId}`,
	};
	const { ...data } = apiService.responseHandler(await apiService.post('todo_categories', body));
	return data;
};

export const updateTodoCategory = async (
	id: number,
	payload: { label: string }
): Promise<ITodoCategory> => {
	const { ...data } = apiService.responseHandler(
		await apiService.put(`todo_categories/${id}`, { label: payload.label })
	);
	return data;
};

export const deleteTodoCategory = async (id: number): Promise<any> => {
	const { ...data } = apiService.responseHandler(await apiService.delete(`todo_categories/${id}`));
	return data;
};
